// Twin hanging-scroll overlay used by the multiplayer entry. Pure
// content — no top bar, no background, no footer. Designed to be
// mounted on top of an existing title screen's chrome (TitleScreenA
// or TitleScreenB) so the page transitions feel like the scrolls
// unrolling over the same backdrop rather than navigating away.
//
// Two visual states:
//   - `mode='pick'`  : both scrolls (Host + Join) shown; user picks
//                      either path. Animations: scaleY 0→1, staggered
//                      150ms, drop-shadow grows in lockstep.
//   - `mode='inscribe'` : the scrolls collapse and a "Choose your hero"
//                      panel takes their place. The user picks a name +
//                      team + icon for THIS match only (per-match
//                      hero). On submit we fire hostLobby / joinByCode
//                      with the identity payload; the server mints a
//                      throwaway Player row and sets the multi cookie.

import {
  useEffect,
  useRef,
  useState,
  type ReactElement,
} from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  DEFAULT_ROUND_TIMER_SECONDS,
  PLAYER_ICON_KEYS,
  RoundTimerOptions,
  type HeroIdentity,
  type PlayerIcon,
  type RoundTimerSeconds,
} from '@origami/shared'
import { GLYPHS } from '../design-system'
import { useMultiStore } from '../multi/multiStore'
import { getCachedPlayer } from '../game/playerApi'
import { useIsFoldOpen, useIsMobile } from './useIsMobile'
import './MultiTwinScrolls.css'

interface MultiTwinScrollsProps {
  /** Tightens insets + font sizes for the mobile / phone fork. */
  compact?: boolean
}

/** Hero pick — same curated set as PlayerCreationScreen so the picker
 *  feels consistent across the app. */
const HERO_PICKS = [
  'fox',
  'pup',
  'owl',
  'koi',
  'wolf',
  'phoenix',
  'mantis',
  'mouse',
] as const satisfies readonly PlayerIcon[]

const HERO_HINTS: Record<(typeof HERO_PICKS)[number], string> = {
  fox: 'the cunning',
  pup: 'the loyal',
  owl: 'the watcher',
  koi: 'the steady stream',
  wolf: 'the pack',
  phoenix: 'the reborn',
  mantis: 'the still blade',
  mouse: 'the small spark',
}

/** Captured intent — what we'll do once the user finishes the
 *  inscribe step. */
type InscribePending =
  | { kind: 'host'; round_timer_seconds: RoundTimerSeconds }
  | { kind: 'join'; lobby_code: string }

export function MultiTwinScrolls({
  compact = false,
}: MultiTwinScrollsProps): ReactElement {
  const error = useMultiStore((s) => s.error)
  const hostLobby = useMultiStore((s) => s.hostLobby)
  const joinByCode = useMultiStore((s) => s.joinByCode)
  const [pending, setPending] = useState<InscribePending | null>(null)
  const sessionAutoSubmittedRef = useRef(false)

  // If the title screen handed off a pending code via sessionStorage
  // (player typed it on the title's inline join input), flip
  // straight into inscribe-for-join mode. Ref guard makes this safe
  // under StrictMode's double-effect.
  useEffect(() => {
    if (sessionAutoSubmittedRef.current) return
    let raw: string | null = null
    try {
      raw = sessionStorage.getItem('origami-pending-join-code')
    } catch {}
    if (!raw) return
    try {
      sessionStorage.removeItem('origami-pending-join-code')
    } catch {}
    const upper = raw.trim().toUpperCase()
    if (!/^[A-Z2-9]{6}$/.test(upper)) return
    sessionAutoSubmittedRef.current = true
    setPending({ kind: 'join', lobby_code: upper })
  }, [])

  const onConfirmHost = (timer: RoundTimerSeconds) =>
    setPending({ kind: 'host', round_timer_seconds: timer })

  const onConfirmJoin = (code: string) =>
    setPending({ kind: 'join', lobby_code: code })

  const onCancelInscribe = () => setPending(null)

  const onSubmitInscribe = (identity: HeroIdentity) => {
    if (!pending) return
    if (pending.kind === 'host') {
      void hostLobby({
        round_timer_seconds: pending.round_timer_seconds,
        identity,
      })
    } else {
      void joinByCode(pending.lobby_code, identity)
    }
    // Leave `pending` set — once the server responds, App-level
    // routing will swap to LobbyScreen and unmount us. If it fails
    // the error toast shows and the user can hit Cancel to retry.
  }

  return (
    <div className={`mts ${compact ? 'mts--compact' : ''}`}>
      <AnimatePresence mode="wait">
        {pending == null ? (
          <PickStage
            key="pick"
            compact={compact}
            onConfirmHost={onConfirmHost}
            onConfirmJoin={onConfirmJoin}
          />
        ) : (
          <InscribePanel
            key="inscribe"
            compact={compact}
            kind={pending.kind}
            onSubmit={onSubmitInscribe}
            onCancel={onCancelInscribe}
          />
        )}
      </AnimatePresence>
      {error && error !== 'no_session' && (
        <div className="mts__error">{humanizeError(error)}</div>
      )}
    </div>
  )
}

// ---- Pick stage (twin scrolls) ---------------------------------------

function PickStage({
  compact,
  onConfirmHost,
  onConfirmJoin,
}: {
  compact: boolean
  onConfirmHost: (timer: RoundTimerSeconds) => void
  onConfirmJoin: (code: string) => void
}): ReactElement {
  return (
    <motion.div
      className="mts__scrolls"
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.18 } }}
    >
      <ScrollPanel variant="host" index={0} compact={compact}>
        <HostInner onConfirm={onConfirmHost} />
      </ScrollPanel>
      <ScrollPanel variant="join" index={1} compact={compact}>
        <JoinInner onConfirm={onConfirmJoin} />
      </ScrollPanel>
    </motion.div>
  )
}

// ---- Scroll panel ----------------------------------------------------

function ScrollPanel({
  variant,
  index,
  compact,
  children,
}: {
  variant: 'host' | 'join'
  index: number
  compact: boolean
  children: React.ReactNode
}): ReactElement {
  // Stagger the unroll so Host (index 0) starts immediately and
  // Join (index 1) follows ~150ms later.
  const delay = index * 0.15
  return (
    <motion.div
      className={`mts-scroll mts-scroll--${variant}${compact ? ' mts-scroll--compact' : ''}`}
      aria-label={variant === 'host' ? 'Host a lobby' : 'Join a lobby'}
      initial={{ scaleY: 0, filter: 'drop-shadow(0 0 0 rgba(0,0,0,0))' }}
      animate={{
        scaleY: 1,
        filter: 'drop-shadow(0 16px 32px rgba(40, 28, 14, 0.28))',
        transition: { delay, duration: 0.5, ease: [0.2, 0.8, 0.2, 1] },
      }}
      exit={{
        scaleY: 0,
        filter: 'drop-shadow(0 0 0 rgba(0,0,0,0))',
        transition: { duration: 0.25, ease: [0.5, 0, 0.8, 0.4] },
      }}
      style={{ transformOrigin: 'top center' }}
    >
      <div className="mts-scroll__art" aria-hidden />
      <div className="mts-scroll__parchment">{children}</div>
    </motion.div>
  )
}

// ---- Host inner content ---------------------------------------------

function HostInner({
  onConfirm,
}: {
  onConfirm: (timer: RoundTimerSeconds) => void
}): ReactElement {
  const [timer, setTimer] = useState<RoundTimerSeconds>(DEFAULT_ROUND_TIMER_SECONDS)
  const foldOpen = useIsFoldOpen()
  const isMobile = useIsMobile()
  const compact = foldOpen || isMobile
  return (
    <>
      <h2 className="mts-card__title">Host</h2>
      <p className="hand-note mts-card__hint">
        {compact
          ? 'Open a table, then share the 6-letter code with friends.'
          : 'Open a fold-table; share the 6-letter scroll-code with your partners.'}
      </p>
      <fieldset className="mts-field">
        <legend className="tiny-cap">Round timer</legend>
        <div className="mts-chips">
          {RoundTimerOptions.map((opt) => (
            <button
              key={opt ?? 'off'}
              type="button"
              className={`mts-chip ${timer === opt ? 'mts-chip--on' : ''}`}
              onClick={() => setTimer(opt)}
            >
              {opt == null ? 'Off' : `${opt}s`}
            </button>
          ))}
        </div>
        <p className="hand-note mts-card__hint" style={{ marginTop: 6 }}>
          {timer == null
            ? 'Rounds advance when every player clicks ready.'
            : 'Rounds auto-resolve when the timer expires.'}
        </p>
      </fieldset>
      <button
        type="button"
        className="mts-cta mts-cta--host"
        onClick={() => onConfirm(timer)}
      >
        Open Fold-Table
      </button>
    </>
  )
}

// ---- Join inner content ---------------------------------------------

function JoinInner({
  onConfirm,
}: {
  onConfirm: (code: string) => void
}): ReactElement {
  const [code, setCode] = useState('')
  const trimmed = code.trim().toUpperCase()
  const valid = /^[A-Z2-9]{6}$/.test(trimmed)
  return (
    <>
      <h2 className="mts-card__title">Join</h2>
      <p className="hand-note mts-card__hint">
        Type the 6-letter scroll-code your host shared.
      </p>
      <fieldset className="mts-field">
        <legend className="tiny-cap">Scroll code</legend>
        <CodeBoxes
          value={trimmed}
          onChange={(v) => setCode(v.slice(0, 6).toUpperCase())}
        />
      </fieldset>
      <button
        type="button"
        className="mts-cta mts-cta--join"
        disabled={!valid}
        onClick={() => valid && onConfirm(trimmed)}
      >
        Enter Fold-Table
      </button>
    </>
  )
}

// ---- 6-box code input ------------------------------------------------

function CodeBoxes({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}): ReactElement {
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <div className="mts-codeboxes" onClick={() => inputRef.current?.focus()}>
      {Array.from({ length: 6 }).map((_, i) => (
        <span
          key={i}
          className={`mts-codebox ${i < value.length ? 'mts-codebox--filled' : ''}`}
        >
          {value[i] ?? ''}
        </span>
      ))}
      <input
        ref={inputRef}
        className="mts-codebox-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={6}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="characters"
        spellCheck={false}
        aria-label="Scroll code"
      />
    </div>
  )
}

// ---- Inscribe panel (hero pick) --------------------------------------

function InscribePanel({
  compact,
  kind,
  onSubmit,
  onCancel,
}: {
  compact: boolean
  kind: 'host' | 'join'
  onSubmit: (identity: HeroIdentity) => void
  onCancel: () => void
}): ReactElement {
  // Pre-fill from the cached solo profile if any so returning users
  // can confirm with one click. Both fields stay editable.
  const cached = getCachedPlayer()
  const cachedIcon = cached?.icon
  const initialIcon =
    cachedIcon && (HERO_PICKS as readonly string[]).includes(cachedIcon)
      ? (cachedIcon as PlayerIcon)
      : null

  const [name, setName] = useState(cached?.display_name ?? '')
  const [team, setTeam] = useState(cached?.team_name ?? '')
  const [icon, setIcon] = useState<PlayerIcon | null>(initialIcon)

  const trimmedName = name.trim()
  const trimmedTeam = team.trim()
  const valid =
    trimmedName.length >= 1 &&
    trimmedName.length <= 24 &&
    trimmedTeam.length >= 1 &&
    trimmedTeam.length <= 24 &&
    icon != null &&
    PLAYER_ICON_KEYS.includes(icon)

  const submit = () => {
    if (!valid || !icon) return
    onSubmit({
      display_name: trimmedName,
      team_name: trimmedTeam,
      icon,
    })
  }

  return (
    <motion.div
      className={`mts-inscribe ${compact ? 'mts-inscribe--compact' : ''}`}
      initial={{ opacity: 0, scale: 0.96, y: 8 }}
      animate={{
        opacity: 1,
        scale: 1,
        y: 0,
        transition: { duration: 0.36, ease: [0.18, 0.85, 0.25, 1] },
      }}
      exit={{ opacity: 0, scale: 0.97, y: -6, transition: { duration: 0.18 } }}
    >
      <div className="mts-inscribe__scroll">
        <div className="mts-inscribe__body">
          <header className="mts-inscribe__header">
            <span className="mts-inscribe__overline tiny-cap">入門 · INDUCT</span>
            <h2 className="mts-inscribe__title">Choose Your Hero</h2>
            <p className="mts-inscribe__sub">
              {kind === 'host'
                ? 'Pick the hero that will host this fold-table.'
                : 'Pick the hero that joins this fold-table.'}
              {' '}A new hero for every match.
            </p>
          </header>

          <div className="mts-inscribe__heroes" role="radiogroup" aria-label="Pick a hero">
            {HERO_PICKS.map((key) => {
              const Glyph = GLYPHS[key]
              const selected = icon === key
              return (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`mts-hero ${selected ? 'mts-hero--on' : ''}`}
                  onClick={() => setIcon(key)}
                >
                  <div className="mts-hero__glyph" aria-hidden>
                    {Glyph ? <Glyph size={compact ? 30 : 44} /> : null}
                  </div>
                  <div className="mts-hero__label">{key}</div>
                  <div className="mts-hero__hint">{HERO_HINTS[key]}</div>
                </button>
              )
            })}
          </div>

          <div className="mts-inscribe__fields">
            <label className="mts-inscribe__field">
              <span className="tiny-cap">Hero name</span>
              <input
                className="mts-inscribe__input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={24}
                placeholder="Name your hero"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && valid) submit()
                  if (e.key === 'Escape') onCancel()
                }}
              />
            </label>
            <label className="mts-inscribe__field">
              <span className="tiny-cap">Team name</span>
              <input
                className="mts-inscribe__input"
                value={team}
                onChange={(e) => setTeam(e.target.value)}
                maxLength={24}
                placeholder="Your team's banner"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && valid) submit()
                  if (e.key === 'Escape') onCancel()
                }}
              />
            </label>
          </div>

          <div className="mts-inscribe__actions">
            <button
              type="button"
              className="mts-inscribe__btn mts-inscribe__btn--ghost"
              onClick={onCancel}
            >
              Back
            </button>
            <button
              type="button"
              className={`mts-inscribe__btn mts-inscribe__btn--primary mts-inscribe__btn--${kind}`}
              disabled={!valid}
              onClick={submit}
            >
              {kind === 'host' ? 'Open Fold-Table' : 'Enter Fold-Table'}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

// ---- Helpers ---------------------------------------------------------

function humanizeError(code: string): string {
  const map: Record<string, string> = {
    lobby_not_found: 'no fold-table found for that scroll-code',
    lobby_full: 'that fold-table is already full',
    lobby_already_started: 'that match has already started',
    already_in_lobby: 'you’re already at another fold-table',
    invalid_body: 'pick a hero name, team, and icon first',
  }
  return map[code] ?? code
}
